/* eslint-disable react-refresh/only-export-components */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';
import { CertKpiGrid } from '../../../-components/certificate-compliance/CertKpiGrid';
import { ExpirationByMonthChart } from '../../../-components/certificate-compliance/ExpirationByMonthChart';
import { ExpirationByTiersChart } from '../../../-components/certificate-compliance/ExpirationByTiersChart';
import { HistoricDataChart } from '../../../-components/certificate-compliance/HistoricDataChart';
import { ReviewStatusChart } from '../../../-components/certificate-compliance/ReviewStatusChart';
import { ExportDialog } from '../../../-components/ExportDialog';
import { ReportToolbar } from '../../../-components/ReportToolbar';
import {
  certMetricsQueryOptions,
  useCertExport,
  useCertMetricsQuery,
} from '../../../-hooks/useCertComplianceQuery';
import { useCertFilterOptions } from '../../../-hooks/useFilterOptions';
import { useReportFilterPreferences } from '../../../-hooks/useReportFilterPreferences';
import { useReportFilters } from '../../../-hooks/useReportFilters';
import { buildCertFilterDefs } from '../../../-utils/filterDefs';

export const Route = createFileRoute(
  '/(client)/report/(pages)/(analytics)/certificate-compliance/',
)({
  validateSearch: z.object({ filter: z.string().optional() }),
  loaderDeps: ({ search }) => ({ filter: search.filter }),
  loader: ({ deps }) => {
    const companyId = useSessionStore.getState().user?.companyId ?? '';
    if (!companyId) return;
    const filter = deps.filter ?? '{}';
    return queryClient.ensureQueryData(
      certMetricsQueryOptions(companyId, filter),
    );
  },
  component: CertCompliancePage,
});

function CertCompliancePage() {
  const { getTranslation: t } = useTranslator({ page: 'certCompliance' });
  const [exportOpen, setExportOpen] = useState(false);

  const { supplierOptions, collaborationStatusOptions, standardOptions } =
    useCertFilterOptions();

  const filterDefs = buildCertFilterDefs({
    supplierOptions,
    collaborationStatusOptions,
    standardOptions,
    t,
  });

  const { filters, setFilters, appliedFilter } = useReportFilters({
    supplierOptions,
    processOptions: [],
    countryOptions: [],
  });

  const { saveFilter } = useReportFilterPreferences('rpt-cert-compliance');
  useEffect(() => {
    saveFilter(appliedFilter);
  }, [appliedFilter, saveFilter]);

  const { data, isLoading } = useCertMetricsQuery(appliedFilter);
  const { handleExport, isExporting } = useCertExport();

  const navigate = useNavigate();

  const DRILLDOWN_ALLOWED_KEYS = new Set([
    'supplierCompanyIds',
    'collaborationStatusIds',
    'standardIds',
    'expirationDate',
    'status',
    'expirationStatus',
    'depthLevel',
  ]);

  const openDrilldown = (
    chartType: string,
    extraFilter?: Record<string, unknown>,
  ) => {
    const base = (() => {
      try {
        return JSON.parse(appliedFilter) as Record<string, unknown>;
      } catch {
        return {};
      }
    })();
    const merged = { ...base, ...extraFilter };
    // Strip non-drilldown fields (startDate, endDate) before navigating
    const drilldownFilter = Object.fromEntries(
      Object.entries(merged).filter(([k]) => DRILLDOWN_ALLOWED_KEYS.has(k)),
    );
    void navigate({
      to: '/report/certificate-compliance/drilldown/$chartType',
      params: { chartType },
      search: { filter: JSON.stringify(drilldownFilter) },
    });
  };

  return (
    <PageContainer className='flex flex-col overflow-hidden'>
      <div className='shrink-0 border-b border-border/60 bg-background px-4 py-3 z-10'>
        <ReportToolbar
          filterDefs={filterDefs}
          filters={filters}
          onFiltersChange={setFilters}
          onExport={() => setExportOpen(true)}
          helpUrl='https://app.intercom.com/a/apps/11060514-introduction-to-the-certificates-compliance-report'
        />
      </div>

      <div className='flex-1 min-h-0 overflow-y-auto'>
        <div className='flex flex-col gap-6 p-4 pb-10'>
          <CertKpiGrid data={data} loading={isLoading} />

          <ExpirationByMonthChart
            data={data?.expirationCertificatesByMonth ?? {}}
            loading={isLoading}
            onBarClick={month =>
              openDrilldown('expirationDate', { expirationDate: month })
            }
          />

          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            <ReviewStatusChart
              data={data?.certificatesByStatus ?? {}}
              loading={isLoading}
              onSliceClick={statusId =>
                openDrilldown('reviewStatus', { status: statusId })
              }
            />
            <ExpirationByTiersChart
              data={data?.certificatesExpirationByProductionSteps}
              loading={isLoading}
              onBarClick={(expirationStatus, depthLevel) =>
                openDrilldown('expirationStatus', {
                  expirationStatus,
                  depthLevel,
                })
              }
            />
          </div>

          <HistoricDataChart baseFilter={appliedFilter} />
        </div>
      </div>

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        onExport={async () => {
          await handleExport(appliedFilter);
        }}
        isExporting={isExporting}
        filters={filters}
        filterDefs={filterDefs}
      />
    </PageContainer>
  );
}
